import {toast} from '@wandb/weave/common/components/elements/Toast';
import {isVersionAlias} from '@wandb/weave/common/util/artifacts';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import copyToClipboard from 'copy-to-clipboard';
import React, {useCallback} from 'react';

import {
  useAddAliasesMutation,
  useDeleteAliasesMutation,
} from '../../generated/graphql';
import {Analytics} from '../../services/analytics';
import {propagateErrorsContext} from '../../util/errors';
import {AddAliasButton, Alias} from '../ArtifactAlias';
import {useIsProjectReadOnly} from '../Registries/common/hooks';
import {useArtifactMembershipAliases} from './hooks';
import {sortAliases} from './utils';

type ArtifactMembershipAliasListProps = {
  artifactCollectionID: string;
  artifactVersion: number;
};

export const ArtifactMembershipAliasList = ({
  artifactCollectionID,
  artifactVersion,
}: ArtifactMembershipAliasListProps) => {
  const [addAliases] = useAddAliasesMutation({
    context: propagateErrorsContext(),
  });
  const [deleteAliases] = useDeleteAliasesMutation({
    context: propagateErrorsContext(),
  });
  const {
    artifactName,
    projectName,
    entityName,
    artifactId,
    artifactVersionAliases,
    isViewerRegistryAdmin,
    protectedAliases,
    availableAliases,
    artifactTypeName,
    artifactIsPortfolio,
    loading,
    refetch,
  } = useArtifactMembershipAliases(artifactCollectionID, artifactVersion);

  const {isProjectReadOnly: readOnly, loading: isProjectReadOnlyLoading} =
    useIsProjectReadOnly(entityName ?? '', projectName ?? '');

  const deleteAlias = useCallback(
    async (alias: string) => {
      if (isVersionAlias({alias})) {
        return null;
      }
      if (protectedAliases.includes(alias) && !isViewerRegistryAdmin) {
        toast('This alias is protected. Only registry admins can delete it.');
        return;
      }
      try {
        await deleteAliases({
          variables: {
            artifactID: artifactId ?? '',
            aliases: [
              {
                alias,
                entityName: entityName ?? '',
                projectName: projectName ?? '',
                artifactCollectionName: artifactName ?? '',
              },
            ],
          },
        });
        await refetch();
      } catch (error) {
        console.error(error);
        toast('Something went wrong when trying to delete this alias.');
      }
      return;
    },
    [
      deleteAliases,
      refetch,
      isViewerRegistryAdmin,
      protectedAliases,
      artifactId,
      entityName,
      projectName,
      artifactName,
    ]
  );

  const createAlias = useCallback(
    async (alias: string) => {
      if (
        isVersionAlias({alias}) ||
        artifactVersionAliases?.find((a: any) => a.alias === alias)
      ) {
        return;
      }
      if (protectedAliases.includes(alias) && !isViewerRegistryAdmin) {
        toast('This alias is protected. Only registry admins can add it.');
        return;
      }
      try {
        await addAliases({
          variables: {
            artifactID: artifactId ?? '',
            aliases: [
              {
                alias,
                entityName: entityName ?? '',
                projectName: projectName ?? '',
                artifactCollectionName: artifactName ?? '',
              },
            ],
          },
        });
        Analytics.track('Artifact interaction', {
          entityName: entityName,
          projectName: projectName,
          artifactCollectionName: artifactName,
          artifactType: artifactTypeName,
          eventType: 'create_alias',
          artifactCollectionType: artifactIsPortfolio
            ? 'portfolio'
            : 'sequence',
        });
        await refetch();
      } catch (error) {
        console.error(error);
        toast('Something went wrong when trying to add this alias.');
      }
    },
    [
      artifactVersionAliases,
      protectedAliases,
      isViewerRegistryAdmin,
      addAliases,
      artifactId,
      entityName,
      projectName,
      artifactName,
      artifactTypeName,
      artifactIsPortfolio,
      refetch,
    ]
  );

  const copyAlias = useCallback(
    (alias: string) => {
      if (loading) {
        return;
      }
      const copyText = `${artifactName}:${alias}`;
      copyToClipboard(copyText);
      toast(`Copied alias '${copyText}' to clipboard`);
    },
    [artifactName, loading]
  );

  if (loading || isProjectReadOnlyLoading) {
    return <></>;
  }

  return (
    <Tailwind>
      <div className="flex flex-wrap gap-y-5">
        {artifactVersionAliases
          .sort((a: {alias: string}, b: {alias: string}) =>
            sortAliases(
              {
                name: a.alias,
                isProtectedAlias: protectedAliases.includes(a.alias),
              },
              {
                name: b.alias,
                isProtectedAlias: protectedAliases.includes(b.alias),
              }
            )
          )
          .map(({alias}: {alias: string}) => {
            const isProtected = protectedAliases.includes(alias);
            if (isProtected && artifactIsPortfolio) {
              return (
                <Tooltip
                  key={`${artifactName}-${alias}`}
                  position={'top center'}
                  content={'This alias is protected'}
                  trigger={
                    <Alias
                      key={artifactName + alias}
                      alias={{alias}}
                      onDelete={
                        !isViewerRegistryAdmin
                          ? undefined
                          : () => deleteAlias(alias)
                      }
                      onClick={() => copyAlias(alias)}
                      isProtected
                    />
                  }
                />
              );
            }
            const isSystem = alias === 'latest' || isVersionAlias({alias});
            return (
              <Alias
                key={artifactName + alias}
                alias={{alias}}
                onDelete={
                  isSystem || readOnly ? undefined : () => deleteAlias(alias)
                }
                onClick={() => copyAlias(alias)}
              />
            );
          })}
        {!readOnly && (
          <AddAliasButton
            aliases={artifactVersionAliases}
            availableAliases={availableAliases.sort((a: any, b: any) =>
              sortAliases(
                {name: a.alias, isProtectedAlias: a.isProtected},
                {name: b.alias, isProtectedAlias: b.isProtected}
              )
            )}
            onCreate={createAlias}
          />
        )}
      </div>
    </Tailwind>
  );
};
